import { Checkbox, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { useEffect } from 'react';
import type { ViewId } from './flow-types';
import { DAYS_OPTIONS, GROUPS, VIEWS } from './flows-constants';

interface FlowsSelectProps {
  pendingViewIds: ViewId[];
  setPendingViewIds: (ids: ViewId[] | ((prev: ViewId[]) => ViewId[])) => void;
  pendingDays: number;
  setPendingDays: (days: number | ((prev: number) => number)) => void;
  selectedViewIds: ViewId[];
  days: number;
  onApply: () => void;
}

export const AssetFlowsFilters = ({ pendingViewIds, setPendingViewIds, pendingDays, setPendingDays, selectedViewIds, days, onApply }: FlowsSelectProps) => {
  const hasChanged = pendingViewIds !== selectedViewIds || pendingDays !== days;

  useEffect(() => {
    if (hasChanged) {
      onApply();
    }
  }, [hasChanged, onApply]);

  const toggle = (id: ViewId): void => {
    const next = pendingViewIds.includes(id) ? pendingViewIds.filter((x) => x !== id) : [...pendingViewIds, id];
    setPendingViewIds(next);
  };

  return (
    <div className="flex gap-6 justify-between items-end mb-6">
      <div className="flex gap-6">
        {GROUPS.map(([g, ids]) => (
          <div key={g} className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 font-semibold">{g}</span>
              {ids.length > 1 && (
                <div className="flex gap-1 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      const idsNotInGroup = pendingViewIds.filter((id) => !ids.includes(id));
                      setPendingViewIds([...idsNotInGroup, ...ids]);
                    }}
                    className="text-blue-500 cursor-pointer hover:underline"
                  >
                    All
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const idsNotInGroup = pendingViewIds.filter((id) => !ids.includes(id));
                      setPendingViewIds(idsNotInGroup);
                    }}
                    className="text-blue-500 cursor-pointer hover:underline"
                  >
                    None
                  </button>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              {ids.map((id) => {
                const checkboxId = id;
                return (
                  <div key={id} className="flex items-center gap-2">
                    <Checkbox id={checkboxId} checked={pendingViewIds.includes(id)} onCheckedChange={() => toggle(id)} />
                    <Label htmlFor={checkboxId} className="text-sm cursor-pointer">
                      {VIEWS[id]?.label}
                    </Label>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-6 items-end">
        <div className="flex flex-col gap-2">
          <span className="text-xs text-gray-400 font-semibold">days</span>
          <div className="flex gap-2">
            <Select value={String(pendingDays)} onValueChange={(value) => setPendingDays(Number(value))}>
              <SelectTrigger className="w-20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DAYS_OPTIONS.map((d) => (
                  <SelectItem key={d} value={String(d)}>
                    {d}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
};
